/*
 * File: ContentEditable.tsx
 * Author: Austin Robinson
 * Date Created: 1/28/2025
 * Copyright (c) DrivingDoc 2025
 */
import {
  ContentEditable as LexicalContentEditable,
  ContentEditableProps as LexicalContentEditableProps,
} from '@lexical/react/LexicalContentEditable';
import { styled } from '@mui/material/styles';

/**
 * Props for the ContentEditable component.
 * Extends all Lexical ContentEditable props while adding custom styling options.
 */
export type ContentEditableProps = LexicalContentEditableProps & {
  /**
   * Horizontal padding (left and right).
   * Can be a number (converted to theme.spacing) or a string for custom CSS.
   *
   * @type {number | string}
   * @default 2 (equivalent to theme.spacing(2))
   */
  px?: number | string;

  /**
   * Vertical padding (top and bottom).
   * Can be a number (converted to theme.spacing) or a string for custom CSS.
   *
   * @type {number | string}
   * @default 2 (equivalent to theme.spacing(2))
   */
  py?: number | string;

  /**
   * Whether to show debug borders on paragraph and placeholder elements.
   * Useful for development and debugging layout issues.
   *
   * @type {boolean}
   * @default false
   */
  showDebugBorders?: boolean;
};

/**
 * Styled ContentEditable component with customizable styling and optional debug borders.
 *
 * This component wraps Lexical's ContentEditable with MUI theming and provides:
 * - Customizable padding and border styling
 * - Automatic paragraph margin reset
 * - Placeholder styling with proper positioning
 * - Optional debug borders for development
 *
 * @example
 * ```tsx
 * // Basic usage with defaults
 * <ContentEditable />
 *
 * // Custom horizontal and vertical padding
 * <ContentEditable
 *   px={4}  // 32px horizontal
 *   py={2}  // 16px vertical
 * />
 *
 * // Custom CSS values
 * <ContentEditable
 *   px="40px"
 *   py="1rem"
 * />
 *
 * // With debug borders enabled
 * <ContentEditable
 *   showDebugBorders={true}
 * />
 * ```
 */
export const ContentEditable = styled(LexicalContentEditable, {
  shouldForwardProp: (prop) => prop !== 'showDebugBorders' && prop !== 'px' && prop !== 'py',
})<ContentEditableProps>(({ theme, px = 2, py = 2, showDebugBorders = false }) => {
  // Convert padding values to CSS
  const paddingX = typeof px === 'number' ? theme.spacing(px) : px;
  const paddingY = typeof py === 'number' ? theme.spacing(py) : py;

  return {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    outline: 'none',
    paddingLeft: paddingX,
    paddingRight: paddingX,
    paddingTop: paddingY,
    paddingBottom: paddingY,
    ...(showDebugBorders && { border: '2px dashed rgba(0, 0, 0, 0.333)' }),

    // Reset default paragraph margins
    '& p': {
      margin: 0,
      padding: 0,
      ...(showDebugBorders && { border: '2px solid red' }),
    },

    // Placeholder styling
    '&[contentEditable="true"]:not(:focus)::before': {
      content: 'attr(placeholder)',
      color: theme.palette.text.disabled,
      pointerEvents: 'none',
      position: 'absolute',
      top: paddingY,
      left: paddingX,
      ...(showDebugBorders && { border: '2px solid blue' }),
    },
  };
});

export default ContentEditable;
